import { ProjectMethodologyEnum } from '@gql/graphql';
import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { methodologyMap } from 'types/methodology';
import { Button, cnMixSpace } from 'uikit';

import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCopyProject } from './hooks/useCopyProject';
import { UerCopyPagesList } from './lib/constants';

import styles from './ProjectCopy.module.css';

const title = 'Копирование проекта';

const loadingStyle = {
    width: 70,
    background: 'transparent',
    boxShadow: 'unset',
};

const contractConfirmation = (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
        <div>
            Проект будет скопирован с параметрами Договора, включая структуру договора и лимиты по прочим затратам.
        </div>
        <div style={{ marginTop: 20 }}>Вы уверены, что хотите скопировать проект?</div>
    </div>
);

const uerConfirmation = (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
        <div>Проект в зависимости от наполнения будет скопирован вместе со следующими страницами:</div>
        <ul className={styles.uerCopyPagesList}>
            {UerCopyPagesList.map((page) => (
                <li key={page.id}>{page.label}</li>
            ))}
        </ul>
        <div style={{ marginTop: 20 }}>Вы уверены, что хотите скопировать проект?</div>
    </div>
);

export const ProjectCopy: React.FC = () => {
    const navigate = useNavigate();
    const { id, methodology } = useProjectParams();
    const { project: projectRules } = useAccess();
    const {
        copyProjectStatus,
        copyProjectError,
        copyProjectData,
        reset,
        isSubmitted,
        setIsSubmitted,
        onCreateReportDownload,
        variables,
        copyProjectMakeRequest,
    } = useCopyProject(id);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({ actionQuery: copyProjectMakeRequest, variables, setIsSubmitted });

    const onCopyButtonClick = useCallback(() => handleOpenConfirmationDialog(), [handleOpenConfirmationDialog]);

    useEffect(() => {
        if (copyProjectData?.copyProject?.result === true && copyProjectData?.copyProject?.data?.uniqueId) {
            reset();
            navigate(`/projects/${methodology}/${copyProjectData?.copyProject?.data?.uniqueId}`);
        }
    }, [
        copyProjectData?.copyProject?.data?.uniqueId,
        copyProjectData?.copyProject?.result,
        methodology,
        navigate,
        reset,
    ]);

    const isCopying = useMemo(() => copyProjectStatus === RequestStatus.FETCHING, [copyProjectStatus]);

    const isDisabled = useMemo(() => !projectRules.canEdit || isCopying, [isCopying, projectRules.canEdit]);

    const isBimProject = useMemo(() => methodology === methodologyMap[ProjectMethodologyEnum.Bim], [methodology]);
    const isUerProject = useMemo(() => methodology === methodologyMap[ProjectMethodologyEnum.Uer], [methodology]);

    const confirmationText = useMemo(() => {
        if (isBimProject) {
            return contractConfirmation;
        }
        if (isUerProject) {
            return uerConfirmation;
        }

        return '';
    }, [isBimProject, isUerProject]);

    return (
        <>
            <Button
                label="Копировать проект"
                size="s"
                onClick={onCopyButtonClick}
                className={cnMixSpace({ mL: 's' })}
                disabled={isDisabled}
                view="secondary"
            />
            <ConfirmationDialog
                textHeader={title}
                textConfirmation={confirmationText}
                cancelButtonText="Отменить"
                confirmButtonText="Копировать"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            <ModalDialog
                title={title}
                onClose={() => false}
                isOpen={isCopying}
                isLoading
                hasOverlay
                style={loadingStyle}
            />
            <ErrorHandlingModal
                titleText={title}
                isSubmitted={isSubmitted}
                statusRequest={copyProjectStatus}
                dataRequest={copyProjectData?.copyProject}
                errorsRequest={copyProjectError}
                handleCloseModalError={reset}
                handleCreateReport={onCreateReportDownload}
            />
        </>
    );
};

ProjectCopy.displayName = 'ProjectCopy';
